import { useFiber } from "@-/com.npmjs.its-fine";
import { use, type Context } from "react";
import {
  PortalContextBridgeScopeContext,
  type PortalContextValues,
} from "#portal-context-bridge-scope-context";

function isContext(value: unknown): value is Context<unknown> {
  return (
    value !== null &&
    typeof value === "object" &&
    "$$typeof" in value &&
    value.$$typeof === Symbol.for("react.context")
  );
}

/** Capture at the source, before moving children to a portal host. */
export function usePortalContextValues({ allowMissingScope = false } = {}): PortalContextValues {
  const inheritedValues = use(PortalContextBridgeScopeContext);
  const fiber = useFiber();
  if (inheritedValues === null) {
    // Only a new transport root opts in. An empty capture is safe; traversing
    // unbounded ancestors here would reintroduce transport subscriptions.
    if (allowMissingScope) {
      return [];
    }
    throw new Error("Portal content requires a PortalContextBridgeScope below its transport.");
  }

  const values = new Map<Context<unknown>, unknown>();
  for (let ancestor = fiber; ancestor != null; ancestor = ancestor.return ?? undefined) {
    const context: unknown = ancestor.type;
    if (context === PortalContextBridgeScopeContext) {
      break;
    }
    if (isContext(context) && !values.has(context)) {
      // React 19 use() may read a variable set of contexts. Filter BEFORE reading:
      // filtering useContextMap's output still subscribes to transport updates.
      values.set(context, use(context));
    }
  }
  for (const [context] of inheritedValues) {
    if (!values.has(context)) {
      values.set(context, use(context));
    }
  }
  return [...values];
}
